import styled, { css, type DefaultTheme } from "styled-components";

type OverlayProps = {
  $open: boolean;
  $side: "left" | "right";
  $width: string;
  $zIndex: number;
  $useAppOffset: boolean;
};

/** Reusable overlay rules (mobile/tablet + desktop overlay) */
const overlayStyles = (p: OverlayProps & { theme: DefaultTheme }) => css`
  position: fixed;
  z-index: ${p.$zIndex + 1};
  background-color: ${p.theme.background.primary};
  overflow-y: auto;
  overscroll-behavior: contain;

  /* Vertical offsets  */
  top: ${
    p.$useAppOffset
      ? `calc(var(--header-height, 0px) + env(safe-area-inset-top, 0px))`
      : "0px"
  };
  bottom: ${
    p.$useAppOffset
      ? `calc(var(--footer-height, 0px) + env(safe-area-inset-bottom, 0px))`
      : "0px"
  };

  /* Horizontal docking */
  ${
    p.$side === "left"
      ? css`
          left: 0;
          right: auto;
          max-width: 100vw;
        `
      : css`
          right: 0;
          left: auto;
          max-width: 100vw;
        `
  }

  width: ${p.$width};

  border-right: ${
    p.$side === "left"
      ? p.$open
        ? `1px solid ${p.theme.border.default}`
        : "0"
      : "0"
  };
  border-left: ${
    p.$side === "right"
      ? p.$open
        ? `1px solid ${p.theme.border.default}`
        : "0"
      : "0"
  };

  box-shadow: ${p.$open ? "0 2px 8px rgba(0,0,0,0.07)" : "none"};
  pointer-events: ${p.$open ? "auto" : "none"};

  transform: ${
    p.$open
      ? "translateX(0)"
      : p.$side === "left"
        ? "translateX(calc(-100% - 2px))"
        : "translateX(calc(100% + 2px))"
  };
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
  will-change: transform;
`;

export const Backdrop = styled.div<{
  $show: boolean;
  $bp: number;
  $desktopOverlay: boolean;
  $useAppOffset: boolean;
}>`
  position: fixed;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${(p) => (p.$show ? 1 : 0)};
  pointer-events: ${(p) => (p.$show ? "auto" : "none")};
  transition: opacity 0.15s ease;

  /* Respect app chrome + iOS safe areas*/
  top: ${(p) =>
    p.$useAppOffset
      ? `calc(var(--header-height, 0px) + env(safe-area-inset-top, 0px))`
      : "0px"};
  bottom: ${(p) =>
    p.$useAppOffset
      ? `calc(var(--footer-height, 0px) + env(safe-area-inset-bottom, 0px))`
      : "0px"};

  /* On desktop: only show if desktop overlay is enabled */
  @media (min-width: ${(p) => p.$bp}px) {
    display: ${(p) => (p.$desktopOverlay ? "block" : "none")};
  }

  /* mobile/tablet */
  @media (max-width: ${(p) => p.$bp - 1}px) {
    top: ${(p) =>
      p.$useAppOffset
        ? `calc(var(--header-height, 0px) + env(safe-area-inset-top, 0px))`
        : "0px"};
    bottom: ${(p) =>
      p.$useAppOffset
        ? `calc(var(--footer-height, 0px) + env(safe-area-inset-bottom, 0px))`
        : "0px"};
  }
`;

export const DrawerContainer = styled.nav<{
  $open: boolean;
  $bp: number;
  $side: "left" | "right";
  $width: string;
  $zIndex: number;
  $useAppOffset: boolean;
  $desktopOverlay: boolean;
  $background: string;
}>`
  /* Desktop */
  @media (min-width: ${(p) => p.$bp}px) {
    ${(p) =>
      p.$desktopOverlay
        ? css`
            ${overlayStyles(p)}
          `
        : css`
            position: relative;
            flex: 0 0 ${p.$width};
            width: ${p.$width};
            height: 100%;
            min-height: 0; /* allow child overflow */
            overflow-y: auto;
            color: inherit;
            background: ${p.$background};
            border-right: ${
              p.$side === "left"
                ? `1px solid ${p.theme.border.default}`
                : "none"
            };
            border-left: ${
              p.$side === "right"
                ? `1px solid ${p.theme.border.default}`
                : "none"
            };
          `}
  }

  /* Mobile/Tablet: overlay drawer (controlled by $open) */
  @media (max-width: ${(p) => p.$bp - 1}px) {
    ${overlayStyles}
  }
  /* mobile/tablet */
  @media (max-width: ${(p) => p.$bp - 1}px) {
    top: ${(p) =>
      p.$useAppOffset
        ? `calc(var(--header-height, 0px) + env(safe-area-inset-top, 0px))`
        : "0px"};
    bottom: ${(p) =>
      p.$useAppOffset
        ? `calc(var(--footer-height, 0px) + env(safe-area-inset-bottom, 0px))`
        : "0px"};
  }

  /* Close button (top-right), show on desktop only if overlay. */
  & > button {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    @media (min-width: ${(p) => p.$bp}px) {
      display: ${(p) => (p.$desktopOverlay ? "inline-flex" : "none")};
    }
  }
`;
